import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { ReactFlow, ReactFlowProvider, Background, Controls, useReactFlow } from '@xyflow/react';
import { useEscapeKey } from '../../hooks/useEscapeKey';
import { useTranslation } from 'react-i18next';
import * as repo from '../../services/repo';
import { nodeTypes } from './nodes';
import { edgeTypes } from './edges';
import { isGroupNodeType } from './utils';
import { nodeSize } from './utils/nodeLayout';
import { logError } from '../../services/eventLog';

interface Props {
  /** 미리 볼 워크플로 id */
  workflowId: string;
  onClose: () => void;
}

/**
 * 실행 노드가 가리키는 **다른 워크플로를 캔버스 그대로** 보여 주는 창.
 *
 * 실행 노드에는 워크플로 id 만 적혀 있어서, 그것이 무엇을 하는 워크플로인지 보려면 캔버스를 닫고
 * 그 워크플로를 열어야 했다(현장 2026-09-22). 여기서는 **열지 않고** 저장된 캔버스만 읽어 그린다 —
 * 지금 편집 중인 캔버스는 건드리지 않는다.
 *
 * 도식이 아니라 **같은 노드 컴포넌트로 그린 진짜 캔버스**다(모양·아이콘·연결선이 캔버스와 같다).
 * 다만 읽기 전용이다: 끌기·연결·선택·삭제가 모두 막혀 있고, 보기(확대·이동)만 된다.
 */
/**
 * 그림을 창에 맞춘다 (화면 없음).
 *
 * **저장된 좌표로 범위를 직접 계산해서** 맞춘다. React Flow 의 `fitView` 는 노드 크기를 다 잰 뒤에야
 * 움직이는데, 그룹(서브플로·반복그룹)이 든 워크플로는 그 시점이 오지 않아 그림이 구석에 1배율로
 * 남았다(실측: 반복그룹·서브플로 샘플). 미리보기는 저장된 것을 그대로 보여 주는 자리라 좌표를 이미
 * 알고 있으므로, 재는 것을 기다릴 이유가 없다.
 */
const FitToBounds: React.FC<{ nodes: any[]; signature: string }> = ({ nodes, signature }) => {
  const { fitBounds } = useReactFlow();
  useEffect(() => {
    if (!nodes.length) return;
    let minX = Infinity; let minY = Infinity; let maxX = -Infinity; let maxY = -Infinity;
    for (const n of nodes) {
      const { width, height } = nodeSize(n);
      const x = n.position?.x ?? 0;
      const y = n.position?.y ?? 0;
      // 그룹 안의 노드는 좌표가 부모 기준이다 — 부모 위치를 더해 같은 좌표계로 본다
      const parent = n.parentId ? nodes.find((p) => p.id === n.parentId) : null;
      const ox = x + (parent?.position?.x ?? 0);
      const oy = y + (parent?.position?.y ?? 0);
      minX = Math.min(minX, ox); minY = Math.min(minY, oy);
      maxX = Math.max(maxX, ox + width); maxY = Math.max(maxY, oy + height);
    }
    if (!Number.isFinite(minX)) return;
    // 노드 아래 이름·부제가 상자 밖에 그려진다 — 아래쪽에 조금 더 여유를 둔다
    const rect = { x: minX, y: minY, width: maxX - minX, height: maxY - minY + 40 };
    // 창이 열리는 그 순간에는 그리는 영역의 크기가 아직 0 이라, 그때 맞추면 배율이 최대로 튄다(실측).
    // 다음 프레임과 잠시 뒤에 다시 맞춘다 — 같은 값으로 여러 번 맞춰도 결과는 같다.
    const fit = () => fitBounds(rect, { padding: 0.12 });
    fit();
    const timers = [setTimeout(fit, 0), setTimeout(fit, 160), setTimeout(fit, 450)];
    return () => timers.forEach(clearTimeout);
  }, [nodes, signature, fitBounds]);
  return null;
};

const WorkflowPreviewModal: React.FC<Props> = ({ workflowId, onClose }) => {
  const { t } = useTranslation();
  useEscapeKey(onClose);
  const wf = useSelector((s: any) => (s.project?.workflows || []).find((w: any) => w.id === workflowId));
  const [state, setState] = useState<{ loading: boolean; error?: string; nodes: any[]; edges: any[] }>(
    { loading: true, nodes: [], edges: [] });

  useEffect(() => {
    let alive = true;
    if (!wf) { setState({ loading: false, error: t('workflow:preview.notFound', { id: workflowId }), nodes: [], edges: [] }); return () => { alive = false; }; }
    repo.loadCanvas(workflowId, wf.curVerNo ?? 0)
      .then((doc) => {
        if (!alive) return;
        setState({ loading: false, nodes: doc?.nodes ?? [], edges: doc?.edges ?? [] });
      })
      .catch((e) => {
        if (!alive) return;
        logError('workflow.open', e, { target: workflowId, where: 'preview' });
        setState({ loading: false, error: (e as Error).message, nodes: [], edges: [] });
      });
    return () => { alive = false; };
  }, [workflowId, wf, t]);

  /**
   * 그리기 전에 **손댈 수 없게** 만든다.
   * 저장된 그대로 넘기면 고른 상태·끌기 플래그가 따라와 편집 중인 것처럼 보인다.
   * 그룹은 자식보다 먼저 와야 React Flow 가 부모를 찾는다(저장 순서가 어떻든 여기서 맞춘다).
   */
  const flowNodes = useMemo(() => {
    const ready = state.nodes.map((n) => ({
      ...n,
      selected: false,
      draggable: false,
      selectable: false,
      connectable: false,
      deletable: false,
      data: { ...(n.data || {}), parentDragging: false },
    }));
    return [...ready.filter((n) => isGroupNodeType(n.type || '')), ...ready.filter((n) => !isGroupNodeType(n.type || ''))];
  }, [state.nodes]);

  const flowEdges = useMemo(
    () => state.edges.map((e) => ({ ...e, selected: false, selectable: false, deletable: false, focusable: false })),
    [state.edges],
  );

  const body = () => {
    if (state.loading) return <div className="text-xs text-gray-500 py-16 text-center">{t('common:ui.loading')}</div>;
    if (state.error) return <div className="text-xs text-red-600 py-16 text-center">{state.error}</div>;
    if (state.nodes.length === 0) return <div className="text-xs text-gray-500 py-16 text-center">{t('workflow:preview.empty')}</div>;
    return (
      // of-preview: 노드에 붙는 편집 장치(추가 +, 삭제·이력 아이콘)와 지금 열려 있는 워크플로의 실행
      // 뱃지를 감춘다 — 노드 컴포넌트가 같아서 그대로 두면 다른 워크플로의 상태가 섞여 보인다.
      <div className="of-preview h-[60vh] border border-gray-200 rounded bg-gray-50">
        <ReactFlowProvider>
          <ReactFlow
            key={workflowId}
            nodes={flowNodes}
            edges={flowEdges}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            fitView
            fitViewOptions={{ padding: 0.15 }}
            minZoom={0.1}
            maxZoom={1.5}
            nodesDraggable={false}
            nodesConnectable={false}
            nodesFocusable={false}
            edgesFocusable={false}
            elementsSelectable={false}
            deleteKeyCode={null}
            selectionKeyCode={null}
            multiSelectionKeyCode={null}
            panOnDrag
            zoomOnScroll
            proOptions={{ hideAttribution: true }}
          >
            <FitToBounds nodes={flowNodes} signature={workflowId} />
            <Background />
            <Controls showInteractive={false} />
          </ReactFlow>
        </ReactFlowProvider>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-30" onMouseDown={onClose}>
      <div className="bg-white rounded shadow-lg w-[900px] max-w-[95vw] max-h-[90vh] flex flex-col" onMouseDown={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <div className="min-w-0">
            <div className="text-sm font-semibold text-gray-800 truncate">{t('workflow:preview.title')}</div>
            <div className="text-xs text-gray-500 truncate">
              {wf ? `${wf.icon ? `${wf.icon} ` : ''}${wf.name} (${workflowId})` : workflowId}
            </div>
          </div>
          <button className="px-2 py-1 text-xs rounded hover:bg-gray-100 text-gray-600" onClick={onClose} title={t('common:ui.close')}>✕</button>
        </div>

        <div className="p-4 overflow-auto">{body()}</div>

        <div className="px-4 py-2 border-t border-gray-200 flex items-center justify-between">
          <span className="text-[0.6875rem] text-gray-500">
            {state.nodes.length > 0 && t('workflow:preview.counts', { nodes: state.nodes.length, edges: state.edges.length })}
          </span>
          <span className="text-[0.6875rem] text-gray-400">{t('workflow:preview.readOnly')}</span>
        </div>
      </div>
    </div>
  );
};

/**
 * 미리보기 창을 띄우는 자리 — 레이아웃에 하나만 둔다.
 * 노드·속성 패널은 `openflow:preview-workflow` 를 쏘기만 하면 된다(노드마다 창을 들고 있지 않게).
 */
export const WorkflowPreviewHost: React.FC = () => {
  const [id, setId] = useState<string | null>(null);
  useEffect(() => {
    const h = (e: Event) => { const d = (e as CustomEvent).detail || {}; if (d.workflowId) setId(String(d.workflowId)); };
    window.addEventListener('openflow:preview-workflow', h);
    return () => window.removeEventListener('openflow:preview-workflow', h);
  }, []);
  return id ? <WorkflowPreviewModal workflowId={id} onClose={() => setId(null)} /> : null;
};

export default WorkflowPreviewModal;
